import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder'
import { TransformNode } from '@babylonjs/core/Meshes/transformNode'
import type { Mesh } from '@babylonjs/core/Meshes/mesh'
import type { Scene } from '@babylonjs/core/scene'
import type { MaterialLibrary } from '../rendering/MaterialLibrary'
import type { RivalSnapshot } from './RivalController'

interface RivalVisual {
  readonly root: TransformNode
  readonly shell: Mesh
  readonly ring: Mesh
  readonly trail: Mesh
}

export class RivalView {
  private readonly visuals: readonly RivalVisual[]

  public constructor(scene: Scene, materials: MaterialLibrary) {
    this.visuals = Array.from({ length: 3 }, (_, index) => {
      const root = new TransformNode(`rival-${index}-root`, scene)
      const shell = MeshBuilder.CreateIcoSphere(`rival-${index}-shell`, { radius: 0.92, subdivisions: 3 }, scene)
      shell.material = materials.hologram
      shell.parent = root
      const ring = MeshBuilder.CreateTorus(`rival-${index}-ring`, { diameter: 2.02, thickness: 0.045, tessellation: 24 }, scene)
      ring.material = materials.edge
      ring.parent = root
      ring.rotation.x = Math.PI / 2 + index * 0.25
      const trail = MeshBuilder.CreateCylinder(`rival-${index}-trail`, {
        height: 1,
        diameterTop: 0.04,
        diameterBottom: 0.42,
        tessellation: 8,
      }, scene)
      trail.material = materials.hologram
      trail.parent = root
      trail.rotation.x = Math.PI / 2
      root.setEnabled(false)
      return { root, shell, ring, trail }
    })
  }

  public update(snapshots: readonly RivalSnapshot[], renderOriginZ: number, speedNormalized: number, reducedMotion: boolean): void {
    for (let index = 0; index < this.visuals.length; index += 1) {
      const visual = this.visuals[index]!
      const snapshot = snapshots[index]
      visual.root.setEnabled(snapshot?.alive === true)
      if (snapshot === undefined || !snapshot.alive) continue
      visual.root.position.set(snapshot.position.x, snapshot.position.y, snapshot.position.z - renderOriginZ)
      visual.shell.rotation.x += 0.025 + speedNormalized * 0.045
      visual.ring.rotation.z += (index % 2 === 0 ? 1 : -1) * (0.012 + speedNormalized * 0.018)
      const length = reducedMotion ? 0.8 : 1.4 + speedNormalized * 3.2
      visual.trail.position.z = -length * 0.5 - 0.7
      visual.trail.scaling.y = length
    }
  }

  public hide(): void {
    for (const visual of this.visuals) visual.root.setEnabled(false)
  }
}
